import React, { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { VideoExtensionRequestRecord } from '@/api/videoApi'
import { useMyExtensionRequestsInfinite } from '@/hooks/querry_hooks/common/useExtensionRequestsQuery'
import { useResponsiveColumns, VIDEO_GRID_COLUMNS } from '@/hooks/common/useResponsiveColumns'
import { useInfiniteScroll } from '@/hooks/common/useInfiniteScroll'
import ExtensionRequestDetailModal from '@/components/common/ExtensionRequestDetailModal'
import ExtensionRequestVideoTile from '@/components/common/ExtensionRequestVideoTile'
import ExtensionRequestsGrid from '@/components/common/ExtensionRequestsGrid'
import Refresh from '@mui/icons-material/Refresh'

const UserExtensionRequestsPanel: React.FC<{
  onViewExtended?: (videoId: string) => Promise<void>
}> = ({ onViewExtended }) => {
  const navigate = useNavigate()
  const columns = useResponsiveColumns(VIDEO_GRID_COLUMNS)
  const [selectedReq, setSelectedReq] = useState<VideoExtensionRequestRecord | null>(null)
  const [viewLoadingId, setViewLoadingId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const handleViewExtended = useCallback(async (videoId: string) => {
    setViewLoadingId(videoId)
    try {
      if (onViewExtended) {
        await onViewExtended(videoId)
      } else {
        navigate(`/dashboard?video=${videoId}`)
      }
    } finally {
      setViewLoadingId(null)
    }
  }, [onViewExtended, navigate])

  const renderItem = useCallback((req: VideoExtensionRequestRecord) => (
    <ExtensionRequestVideoTile
      req={req}
      onTileClick={() => setSelectedReq(req)}
      onViewExtended={req.extended_video_id ? () => handleViewExtended(req.extended_video_id!) : undefined}
      viewLoading={viewLoadingId === req.extended_video_id}
    />
  ), [handleViewExtended, viewLoadingId])

  const getItemKey = useCallback((req: VideoExtensionRequestRecord) => req.id, [])

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isError,
    refetch,
  } = useMyExtensionRequestsInfinite(statusFilter === 'all' ? undefined : statusFilter)

  const items = data?.pages.flatMap(p => p.items) ?? []
  const total = data?.pages[0]?.total ?? 0
  const isLoading = isFetching && !isFetchingNextPage
  const allLoaded = !hasNextPage && items.length > 0

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetching,
    fetchNextPage,
  })

  if (!isFetching && total === 0 && items.length === 0 && statusFilter === 'all') return null

  const STATUS_FILTERS = [
    { value: 'all', label: 'All' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' },
  ]

  return (
    <>
    <div className="px-4 lg:px-8 mb-8">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">My Extension Requests</h3>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
        >
          <Refresh style={{ fontSize: '16px' }} className={isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Status filter pills */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {STATUS_FILTERS.map(f => (
          <button
            type="button"
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition border ${
              statusFilter === f.value
                ? 'text-white border-transparent'
                : 'text-gray-500 bg-white border-gray-200 hover:border-gray-400'
            }`}
            style={statusFilter === f.value ? { background: 'linear-gradient(to right, #F69A7E, #939BEC)', border: 'none' } : {}}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isError && (
        <div className="mb-4 px-4 py-2 text-xs text-red-600 bg-red-50 rounded-lg">Failed to load your extension requests</div>
      )}

      <ExtensionRequestsGrid
        items={items}
        columns={columns}
        renderItem={renderItem}
        getItemKey={getItemKey}
        sentinelRef={sentinelRef}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        isError={false}
        allLoaded={allLoaded}
        emptyMessage="No extension requests yet"
      />
    </div>

    {selectedReq && (
      <ExtensionRequestDetailModal
        req={selectedReq}
        onClose={() => setSelectedReq(null)}
        onViewExtended={onViewExtended}
      />
    )}
  </>
  )
}

export default UserExtensionRequestsPanel
